/**
 * FootprintCompareStrip — the investor-demo centerpiece.
 *
 * Side-by-side "Sparse footprint" vs "Signature footprint" demo that renders
 * two fixture creator-match previews for the same campaign brief. The
 * investor instantly sees: no footprint → bad matches → bad waves.
 *
 * This is a pure client-side fixture component (no backend dependency) because
 * its purpose is to be deterministic and demo-stable. Never hits the network.
 *
 * Usage:
 *   <FootprintCompareStrip />   ← drop anywhere on the Footprint or Home page
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, TrendingUp, XCircle, CheckCircle2 } from 'lucide-react';
import CreatorAvatar from '../creators/CreatorAvatar';

// ── fixture creators: same SKU brief (eye cream), different match quality ──
const SPARSE_MATCHES = [
  { handle: '@beauty_is_life_',  platform: 'TikTok',    followers: 412_000, niche: 'Beauty (generic)', match: 58, reason: 'Keyword match: "skincare"', tags: ['generic'] },
  { handle: '@tiktok_dance_x',   platform: 'TikTok',    followers: 982_000, niche: 'Dance / lifestyle', match: 51, reason: 'Follower range only', tags: ['off-niche'] },
  { handle: '@daily_vibe_221',   platform: 'Instagram', followers: 145_000, niche: 'Lifestyle lifestyle', match: 47, reason: 'Keyword match: "beauty"', tags: ['low-confidence'] },
  { handle: '@mom.of.three',     platform: 'Instagram', followers:  58_000, niche: 'Mom content',      match: 44, reason: 'Lookalike proximity', tags: ['broad'] },
  { handle: '@random_routine',   platform: 'TikTok',    followers: 221_000, niche: 'Routines',         match: 39, reason: 'Demographic overlap', tags: ['broad'] },
];

const SIGNATURE_MATCHES = [
  { handle: '@ninaroutines',  platform: 'TikTok',    followers: 841_000, niche: '30+ skincare confessions', match: 94, reason: 'CRM overlap · Glowmart retail geo · Competitor audience', tags: ['CRM match', 'retail', 'competitor'] },
  { handle: '@dr.melaskin',   platform: 'Instagram', followers: 512_000, niche: 'Dermatology education',  match: 92, reason: 'Patient-Zero historical · Store hyperlocal · SKU angle',  tags: ['historical', 'local'] },
  { handle: '@the_auradiary', platform: 'TikTok',    followers: 287_000, niche: 'Hyperpigmentation POV',  match: 90, reason: 'Audience demo × your Core Audience · CRM lookalike',     tags: ['audience', 'CRM match'] },
  { handle: '@mornings.w.jo', platform: 'YouTube',   followers: 198_000, niche: 'Morning routines',       match: 87, reason: 'Conversion pixel cohort · Retail geo: Beautyhaus Union Sq', tags: ['conv', 'retail'] },
  { handle: '@lou.the.derm',  platform: 'Instagram', followers: 165_000, niche: 'Science-backed skincare', match: 85, reason: 'Vertical expert · Competitor whitespace · Patient-Zero reach',  tags: ['expert', 'competitor'] },
];

const REASON_COLORS = {
  'CRM match':   '#FF4D94',
  'retail':      '#00D4FF',
  'competitor':  '#A78BFA',
  'historical':  '#4ade80',
  'local':       '#00D4FF',
  'audience':    '#FF4D94',
  'conv':        '#FACC15',
  'expert':      '#4ade80',
  // iter 59: 'brand-safe' removed as a per-creator reason color. Brand safety
  // is a platform-level guarantee on WAVE, not a per-creator filter.
  'generic':     '#6B7280',
  'off-niche':   '#FF6B6B',
  'low-confidence': '#FF6B6B',
  'broad':       '#6B7280',
};

const STATS = {
  sparse: {
    band: 'Sparse',
    score: 28,
    bandColor: '#FF6B6B',
    avg_match: 47.8,
    projected_reach: '310K',
    projected_roas: '1.6×',
    health_score: 28,
    cdp_dims: '3/12',
    note: 'No customer segments. No performance history. No Core Audience per SKU. Matches fall back to keyword + follower bands — the same a generic influencer tool produces.',
  },
  signature: {
    band: 'Signature',
    score: 100,
    bandColor: '#FF4D94',
    avg_match: 89.6,
    projected_reach: '3.7M',
    projected_roas: '6.4×',
    health_score: 100,
    cdp_dims: '12/12',
    note: '340K customer rows, 32 past campaigns, 94 store locations, 47 SKUs tracked, per-SKU Core Audience. Match engine uses customer lookalikes + geographic priority + creative-insight history + audience-overlap diversity.',
  },
};

export default function FootprintCompareStrip() {
  const [active, setActive] = useState('signature'); // default to the "aha" side

  return (
    <section
      className="rounded-2xl bg-[#0F0F0F] border border-[#1f1f1f] overflow-hidden"
      data-testid="footprint-compare-strip"
    >
      <header className="px-5 py-4 border-b border-[#1f1f1f] flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#FF4D94] font-bold">Why 1st party data matters</p>
          <h3 className="text-lg font-black text-white mt-0.5 leading-tight">Same brief, different data.</h3>
          <p className="text-[11px] text-gray-400 leading-snug mt-1 max-w-prose">
            A CPG client asked WAVE to match 5 creators for an eye-cream SKU. On the left: what a brand without 1st party data gets.
            On the right: what a brand with full 1st party data gets — the same match engine, radically different output.
          </p>
        </div>

        {/* Toggle */}
        <div className="inline-flex rounded-full bg-[#0A0A0A] border border-[#1f1f1f] p-1" data-testid="compare-toggle">
          <ToggleBtn
            active={active === 'sparse'}
            onClick={() => setActive('sparse')}
            label="Without 1st party data"
            color="#FF6B6B"
            testid="toggle-sparse"
          />
          <ToggleBtn
            active={active === 'signature'}
            onClick={() => setActive('signature')}
            label="With 1st party data"
            color="#FF4D94"
            testid="toggle-signature"
          />
        </div>
      </header>

      <div className="grid md:grid-cols-2 gap-px bg-[#1f1f1f]">
        <SidePanel
          side="sparse"
          matches={SPARSE_MATCHES}
          stats={STATS.sparse}
          highlighted={active === 'sparse'}
        />
        <SidePanel
          side="signature"
          matches={SIGNATURE_MATCHES}
          stats={STATS.signature}
          highlighted={active === 'signature'}
        />
      </div>

      <footer className="px-5 py-3 border-t border-[#1f1f1f] flex items-center justify-between gap-3 flex-wrap bg-[#0A0A0A]">
        <p className="text-[11px] text-gray-400">
          <span className="text-[#FF4D94] font-bold">+41.8 pts</span> average match-quality lift · <span className="text-[#00D4FF] font-bold">12×</span> reach · <span className="text-[#4ade80] font-bold">4×</span> ROAS
        </p>
        <Link to="/data-room" data-testid="compare-build-cta"
              className="inline-flex items-center gap-1 text-[11px] font-bold text-[#FF4D94] hover:underline">
          Build your 1st Party Data <ArrowRight className="h-3 w-3" />
        </Link>
      </footer>
    </section>
  );
}

function ToggleBtn({ active, onClick, label, color, testid }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testid}
      className="px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.15em] font-bold transition-all"
      style={{
        color: active ? '#0A0A0A' : '#9CA3AF',
        background: active ? color : 'transparent',
        boxShadow: active ? `0 0 16px ${color}60` : 'none',
      }}
    >
      {label}
    </button>
  );
}

function SidePanel({ side, matches, stats, highlighted }) {
  const avgMatch = stats.avg_match;
  return (
    <div
      className="bg-[#0F0F0F] p-4 transition-opacity"
      style={{ opacity: highlighted ? 1 : 0.55 }}
      data-testid={`compare-panel-${side}`}
    >
      {/* Stat ribbon */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {side === 'sparse'
            ? <XCircle className="h-4 w-4 text-[#FF6B6B]" />
            : <CheckCircle2 className="h-4 w-4 text-[#FF4D94]" />}
          <div>
            <p className="text-[9px] uppercase tracking-[0.2em] text-gray-500 font-bold">{stats.band} footprint</p>
            <p className="text-xl font-black tabular-nums" style={{ color: stats.bandColor }}>
              {stats.score}<span className="text-xs text-gray-500">/100</span>
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[9px] uppercase tracking-[0.2em] text-gray-500 font-bold">Avg match</p>
          <p className="text-xl font-black tabular-nums text-white">{avgMatch.toFixed(1)}%</p>
        </div>
      </div>

      {/* Projections strip — Data Health Score replaces the retired
          CDP-equivalent $ stat (2026-05-26). Honest, explainable, real. */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <MiniStat label="Reach"             value={stats.projected_reach} />
        <MiniStat label="ROAS"              value={stats.projected_roas}  />
        <MiniStat label="Data Health Score" value={`${stats.health_score}/100`} />
      </div>

      {/* Creator list */}
      <ol className="space-y-1.5">
        {matches.map((m) => (
          <li key={m.handle}
              data-testid={`compare-${side}-${m.handle.replace(/[@.]/g, '')}`}
              className="flex items-center gap-2.5 rounded-xl px-2.5 py-2 bg-[#0A0A0A] border border-[#1f1f1f]">
            <CreatorAvatar handle={m.handle} size={30} />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold text-white leading-tight truncate">{m.handle}</p>
              <p className="text-[9px] text-gray-500 leading-tight truncate">
                {m.platform} · {(m.followers / 1000).toFixed(0)}K · {m.niche}
              </p>
              <div className="flex flex-wrap gap-1 mt-1">
                {m.tags.slice(0, 3).map(t => (
                  <span key={t} className="text-[8.5px] px-1.5 py-[1px] rounded-full font-bold"
                        style={{
                          background: `${REASON_COLORS[t] || '#6B7280'}18`,
                          color: REASON_COLORS[t] || '#6B7280',
                          border: `1px solid ${REASON_COLORS[t] || '#6B7280'}30`,
                        }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-sm font-black tabular-nums" style={{ color: stats.bandColor }}>{m.match}%</p>
              <p className="text-[9px] text-gray-600">match</p>
            </div>
          </li>
        ))}
      </ol>

      <p className="text-[10px] text-gray-500 leading-snug mt-3 flex items-start gap-1.5">
        {side === 'sparse'
          ? <TrendingUp className="h-3 w-3 text-[#FF6B6B] flex-shrink-0 mt-0.5 rotate-180" />
          : <Sparkles className="h-3 w-3 text-[#FF4D94] flex-shrink-0 mt-0.5" />}
        {stats.note}
      </p>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg p-2 bg-[#0A0A0A] border border-[#1f1f1f]">
      <p className="text-[8.5px] uppercase tracking-[0.15em] text-gray-500 font-bold">{label}</p>
      <p className="text-sm font-black tabular-nums text-white">{value}</p>
    </div>
  );
}
